import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import RouteIcon from '@mui/icons-material/Route';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Fragment,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import { BASE } from 'common/src/routes/BaseRoutes';
import { isPathActive } from 'common/src/routes/helpers';

import useAuth from 'portal/src/auth/useAuth';
import useLuzmo from 'portal/src/providers/useLuzmo';
import { NEW_SECONDARY_COLOR } from 'portal/src/theme/constants';

import ImageLoader from '../../components/common/ImageLoader';
import useProductMenuItems, {
  INavigationMenuItem,
  ITopLevelNavigationItem,
} from '../../navigation/useProductMenuItems';
import OrganizationSwitcher from '../header/OrganizationSwitcher';
import { useLayoutPreference } from '../LayoutPreference/useLayoutPreference';

const logoPng = import.meta.env.VITE_LOGO_SMALL || 'ne_logo.png';

const MENU_WIDTH = 85;
const FLYOUT_WIDTH = 255;
const PINNED_MENU_WIDTH = MENU_WIDTH + FLYOUT_WIDTH;

const Menu = ({
  open,
  menuItemClick,
}: {
  open: boolean;
  menuItemClick: () => void;
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const menuItems = useProductMenuItems();
  const { hasOrgSwitchingAccess, hasClientSystemAdminAccess } = useAuth();
  const {
    isMenuPinnedOpen,
    setIsMenuPinnedOpen,
    setTabletMenuItemId,
    tabletMenuItemId,
  } = useLayoutPreference();
  const {
    customDashboards,
    error: customDashboardError,
    isLoading: customDashboardLoading,
    refreshData,
  } = useLuzmo();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set([
      'Summary',
      'Usage',
      'Costs',
      'Portfolio',
      'Electric',
      'Water',
      'Natural Gas',
    ]),
  );
  const [hoveredMenuItemId, setHoveredMenuItemId] = useState<string | null>(
    null,
  );
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const setActiveMenuItemId = useCallback(
    (menuItemId: string | null) => {
      if (isTablet) {
        setTabletMenuItemId(menuItemId);
        return;
      }

      setHoveredMenuItemId(menuItemId);
    },
    [isTablet, setTabletMenuItemId],
  );

  useEffect(() => {
    const closeUnpinnedFlyout = (target: EventTarget | null) => {
      if (isTablet || isMenuPinnedOpen) {
        return;
      }

      if (!(target instanceof Node)) {
        return;
      }

      if (menuContainerRef.current?.contains(target)) {
        return;
      }

      setActiveMenuItemId(null);
    };

    const handlePointerDown = (event: PointerEvent) => {
      closeUnpinnedFlyout(event.target);
    };

    const handleFocusIn = (event: FocusEvent) => {
      closeUnpinnedFlyout(event.target);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [isTablet, isMenuPinnedOpen, setActiveMenuItemId]);

  const toggleGroupExpanded = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const selectedMainItem =
    menuItems.find((item) => item.matchesPath(pathname)) ?? menuItems[0];

  const activeMenuItem = useMemo(() => {
    if (isTablet) {
      const currentMenuItemId = tabletMenuItemId;

      return (
        menuItems.find((item) => item.id === currentMenuItemId) ??
        selectedMainItem ??
        null
      );
    }

    const currentMenuItemId =
      hoveredMenuItemId ?? (isMenuPinnedOpen ? selectedMainItem?.id : null);

    return menuItems.find((item) => item.id === currentMenuItemId) ?? null;
  }, [
    hoveredMenuItemId,
    tabletMenuItemId,
    isMenuPinnedOpen,
    menuItems,
    isTablet,
    selectedMainItem,
  ]);

  const topLevelIcon = (itemId: ITopLevelNavigationItem['id']) => {
    switch (itemId) {
      case 'dashboards':
        return <DashboardOutlinedIcon sx={{ fontSize: 28 }} />;
      case 'evCharging':
        return <PowerOutlinedIcon sx={{ fontSize: 28 }} />;
      case 'reports':
        return <SummarizeOutlinedIcon sx={{ fontSize: 28 }} />;
      case 'smartMeters':
        return <SpeedOutlinedIcon sx={{ fontSize: 28 }} />;
      case 'billAutomation':
        return <RouteIcon sx={{ fontSize: 28 }} />;
      case 'custom':
        return <DashboardCustomizeOutlinedIcon sx={{ fontSize: 28 }} />;
      case 'organization':
        return <BusinessOutlinedIcon sx={{ fontSize: 28 }} />;
      case 'systemAdministration':
        return <SettingsOutlinedIcon sx={{ fontSize: 28 }} />;
      default:
        return <DashboardOutlinedIcon sx={{ fontSize: 28 }} />;
    }
  };

  const navigateTo = (link: string) => {
    navigate(link || '/');
    menuItemClick();
  };

  const isPlainLeftClick = (event: MouseEvent<HTMLElement>) =>
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;

  const togglePinnedMenu = () => {
    if (isTablet) {
      return;
    }

    if (isMenuPinnedOpen) {
      setIsMenuPinnedOpen(false);
      return;
    }

    setIsMenuPinnedOpen(true);

    const menuIdToPin = activeMenuItem?.id ?? selectedMainItem?.id;
    if (menuIdToPin) {
      setActiveMenuItemId(menuIdToPin);
    }
  };

  const handleMenuMouseLeave = () => {
    if (isTablet) {
      return;
    }

    if (!isMenuPinnedOpen) {
      setActiveMenuItemId(null);
    }
  };

  const renderFlyoutList = (
    items: INavigationMenuItem[],
    depth = 0,
  ): React.ReactNode => {
    return items.map((item) => {
      const isActive = isPathActive(pathname, item.link);
      const key = `${item.title}-${item.link}`;
      const isDashboardParent =
        depth === 0 && activeMenuItem?.id === 'dashboards' && item.group;
      const isNonClickableParent = item.isClickable === false;
      const hasChildren = !!item.children?.length;
      const isExpanded = expandedGroups.has(item.group || '');

      // For dashboard accordion groups
      if (isDashboardParent) {
        return (
          <Box key={key}>
            <ListItemButton
              onClick={() => toggleGroupExpanded(item.group || '')}
              sx={{
                borderRadius: 1,
                ml: depth * 2,
                mr: 1,
                pl: depth ? 2 : 1,
                py: 0.75,
                color: 'text.primary',
                backgroundColor: 'transparent',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 'inherit',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out',
                  ml: 1,
                }}
              >
                <KeyboardArrowDownIcon />
              </Box>
            </ListItemButton>
            {isExpanded &&
              hasChildren &&
              renderFlyoutList(item.children, depth + 1)}
          </Box>
        );
      }

      // For non-clickable parent items (Reports, Organization, etc.)
      if (isNonClickableParent && hasChildren) {
        return (
          <Box key={key}>
            <ListItemButton
              disabled
              sx={{
                borderRadius: 1,
                ml: depth * 2,
                mr: 1,
                pl: depth ? 2 : 1,
                py: 0.75,
                color: 'text.primary',
                backgroundColor: 'transparent',
                fontWeight: 600,
                cursor: 'default',
                '&.Mui-disabled': {
                  opacity: 1,
                  color: 'text.primary',
                  fontWeight: 600,
                },
              }}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 'inherit',
                }}
              />
            </ListItemButton>
            {renderFlyoutList(item.children, depth + 1)}
          </Box>
        );
      }

      // For clickable items (sub-items and dashboard items when not an accordion parent)
      return (
        <Box key={key}>
          {item.dividerBefore && (
            <Divider sx={{ mx: 3.5, borderColor: 'divider' }} />
          )}
          <ListItemButton
            component="a"
            href={item.link}
            onClick={(event) => {
              if (!isPlainLeftClick(event)) {
                return;
              }

              event.preventDefault();
              navigateTo(item.link);
            }}
            sx={{
              borderRadius: 1,
              ml: depth * 2,
              mr: 1,
              pl: depth ? 2 : 1,
              py: depth ? 0.25 : 0.75,
              color: 'text.primary',
              backgroundColor: 'transparent',
              fontWeight: depth === 0 ? 600 : 400,
              cursor: 'pointer',
              paddingBottom:
                isActive && depth > 0 ? 'calc(0.25rem - 3px)' : undefined,
              '&:hover': {
                backgroundColor:
                  depth > 0 ? theme.palette.action.hover : 'transparent',
              },
            }}
          >
            <ListItemText
              primary={
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: depth ? 13 : 14,
                    fontWeight: depth === 0 ? 600 : 400,
                  }}
                >
                  {item.title}
                  {isActive && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '3px',
                        backgroundColor: NEW_SECONDARY_COLOR,
                        display: 'block',
                      }}
                    />
                  )}
                </span>
              }
              primaryTypographyProps={{
                component: 'div',
                sx: {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </ListItemButton>
          {!!item.children?.length &&
            renderFlyoutList(item.children, depth + 1)}
        </Box>
      );
    });
  };

  const renderCustomFlyoutList = () => {
    // Render the "Custom Dashboards" parent header
    const parentHeader = (
      <ListItemButton
        disabled
        key="custom-dashboards-parent"
        sx={{
          borderRadius: 1,
          mr: 1,
          pl: 1,
          py: 0.75,
          color: 'text.primary',
          backgroundColor: 'transparent',
          fontWeight: 600,
          cursor: 'default',
          '&.Mui-disabled': {
            opacity: 1,
            color: 'text.primary',
            fontWeight: 600,
          },
        }}
      >
        <ListItemText
          primary="Custom Dashboards"
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: 'inherit',
            noWrap: true,
          }}
        />
      </ListItemButton>
    );

    if (customDashboardLoading) {
      return (
        <>
          {parentHeader}
          <ListItemButton disabled sx={{ borderRadius: 1, ml: 2, mr: 1 }}>
            <ListItemText
              primary="Loading custom dashboards..."
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </ListItemButton>
        </>
      );
    }

    if (customDashboardError) {
      return (
        <>
          {parentHeader}
          <Box sx={{ px: 1, py: 0.5, ml: 2 }}>
            <Typography color="error" fontSize={13} sx={{ mb: 1 }}>
              Unable to load custom dashboards.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void refreshData()}
            >
              Retry
            </Button>
          </Box>
        </>
      );
    }

    if (customDashboards.length === 0) {
      return (
        <>
          {parentHeader}
          <ListItemButton disabled sx={{ borderRadius: 1, ml: 2, mr: 1 }}>
            <ListItemText
              primary="No custom dashboards yet."
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </ListItemButton>
        </>
      );
    }

    const dashboardItems = customDashboards.map((dashboard) => {
      const link = `${BASE.CUSTOM_DASHBOARDS}/${dashboard.id}`;
      const isActive = pathname === link;

      return (
        <ListItemButton
          key={dashboard.id}
          component="a"
          href={link}
          onClick={(event) => {
            if (!isPlainLeftClick(event)) {
              return;
            }

            event.preventDefault();
            navigateTo(link);
          }}
          sx={{
            borderRadius: 1,
            ml: 2,
            mr: 1,
            pl: 2,
            py: 0.25,
            color: 'text.primary',
            backgroundColor: 'transparent',
            fontWeight: 400,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemText
            primary={
              <span
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  fontSize: 13,
                  fontWeight: 400,
                }}
              >
                {dashboard.name}
                {isActive && (
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      backgroundColor: NEW_SECONDARY_COLOR,
                      display: 'block',
                    }}
                  />
                )}
              </span>
            }
            primaryTypographyProps={{
              component: 'div',
              sx: {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </ListItemButton>
      );
    });

    return (
      <>
        {parentHeader}
        {dashboardItems}
      </>
    );
  };

  const mainRail = (
    <Box display="flex" flexDirection="column" height="100%" width={MENU_WIDTH}>
      <Box padding={1.5} display="flex" justifyContent="center">
        <ImageLoader
          src={`/${logoPng}`}
          alt="logo"
          effect="opacity"
          effectDuration={600}
          wrapperStyle={{
            transitionDelay: '1s',
            width: '100%',
            maxWidth: '45px',
          }}
        />
      </Box>
      <List
        component={'nav'}
        sx={{
          marginTop: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {hasOrgSwitchingAccess() && (
          <>
            <Box component={ListItemButton} sx={{ p: 0, mb: 1 }}>
              <OrganizationSwitcher />
            </Box>
            <Divider
              sx={{
                borderColor: 'rgba(255,255,255,0.24)',
                my: 1,
                width: '80%',
              }}
            />
          </>
        )}
        {menuItems.map((item) => {
          const isActive = item.matchesPath(pathname);
          const isHovered = activeMenuItem?.id === item.id;
          const isBeforeDivider = item.id === 'evCharging';

          return (
            <Fragment key={item.id}>
              {item.dividerBefore && (
                <Divider
                  sx={{
                    borderColor: 'rgba(255,255,255,0.24)',
                    my: 1,
                    width: '80%',
                  }}
                />
              )}
              <Box
                sx={{
                  mb: isBeforeDivider ? 1 : 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  position: 'relative',
                }}
              >
                <ListItemButton
                  component="a"
                  href={item.link}
                  aria-label={item.title}
                  onMouseEnter={() => setActiveMenuItemId(item.id)}
                  onFocus={() => setActiveMenuItemId(item.id)}
                  onClick={(event: MouseEvent<HTMLElement>) => {
                    if (!isPlainLeftClick(event)) {
                      return;
                    }

                    event.preventDefault();

                    if (isTablet) {
                      navigate(item.link);
                      setActiveMenuItemId(item.id);
                      menuItemClick();
                      return;
                    }

                    navigateTo(item.link);
                  }}
                  sx={{
                    width: 35,
                    height: 35,
                    mb: 0.5,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                    backgroundColor: isActive
                      ? 'secondary.dark'
                      : 'transparent',
                    border: isActive ? '1px solid' : '1px solid transparent',
                    borderColor:
                      isActive || isHovered
                        ? 'secondary.contrastText'
                        : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderColor: '#ffffff',
                    },
                  }}
                >
                  {topLevelIcon(item.id)}
                </ListItemButton>
                <Typography
                  sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}
                >
                  {item.title}
                </Typography>
              </Box>
            </Fragment>
          );
        })}
      </List>
    </Box>
  );

  const flyout = activeMenuItem ? (
    <Box
      sx={{
        position: isTablet ? 'relative' : 'absolute',
        left: isTablet ? 'auto' : MENU_WIDTH,
        top: 0,
        width: FLYOUT_WIDTH,
        height: '100%',
        bgcolor: 'background.paper',
        borderLeft: isTablet ? 'none' : '1px solid',
        borderColor: 'divider',
        boxShadow: isTablet ? 0 : 4,
        display: 'flex',
        flexDirection: 'column',
        zIndex: isTablet ? 'auto' : 1100,
      }}
      onMouseEnter={() => setActiveMenuItemId(activeMenuItem.id)}
    >
      {!isTablet && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Tooltip
            title={isMenuPinnedOpen ? 'Unlock drawer' : 'Lock drawer open'}
          >
            <IconButton
              onClick={togglePinnedMenu}
              size="small"
              sx={{
                color: isMenuPinnedOpen ? 'warning.main' : 'text.secondary',
                borderRadius: 1,
                border: '1px solid transparent',
                borderColor: 'text.secondary',
                p: 0.5,
                width: 30,
                height: 30,
                '&:hover': {
                  borderColor: 'text.secondary',
                },
              }}
            >
              {isMenuPinnedOpen ? <PushPinIcon /> : <PushPinOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1, px: 1 }}>
        <List component="nav" sx={{ pt: 0 }}>
          {activeMenuItem.id === 'custom'
            ? renderCustomFlyoutList()
            : renderFlyoutList(activeMenuItem.menuItems)}
        </List>
      </Box>
    </Box>
  ) : null;

  if (isTablet) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={() => menuItemClick()}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: MENU_WIDTH + FLYOUT_WIDTH,
            backgroundColor: 'primary.main',
            overflow: 'hidden',
          },
        }}
      >
        <Box display="flex" height="100%" width="100%">
          {mainRail}
          {flyout}
        </Box>
      </Drawer>
    );
  }

  return (
    <Box
      ref={menuContainerRef}
      sx={{
        backgroundColor: 'primary.main',
        height: '100vh',
        py: 1,
        width: isMenuPinnedOpen ? PINNED_MENU_WIDTH : MENU_WIDTH,
        position: 'relative',
        overflow: 'visible',
        zIndex: 1000,
        transition: 'width 0.3s ease-in-out',
      }}
      onMouseLeave={handleMenuMouseLeave}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !isMenuPinnedOpen) {
          setActiveMenuItemId(null);
        }
      }}
    >
      {mainRail}
      {flyout}
    </Box>
  );
};

export default Menu;
